import { audio } from '../audio/audio';
import {
  MASS_KINDS,
  MASS_SPECS,
  RECEIVER_RADIUS,
  SOURCE_RADIUS,
  WALL_HALF,
  WORLD_H,
  WORLD_W,
  colorName,
} from '../core/constants';
import { emptyLevel, traceLevel, validateLevel, weightOf, wins } from '../core/level';
import { decodeLevel, encodeLevel, quantizeLevel } from '../core/sharecode';
import type { Level, Mass, MassKind } from '../core/types';
import { store } from '../game/store';
import { beamColor } from '../render/palette';
import { trayIcon } from '../render/sprites';
import { type Screen, app, markReady } from './app';
import { openDialog, openHelp } from './dialogs';
import { ICONS, announce, h, icon } from './dom';
import { gameScreen } from './game';

type Tool =
  'select' | 'source' | 'receiver' | 'wall' | 'rock' | 'dust' | 'nebula' | MassKind | 'erase';
type ElType = 'source' | 'receiver' | 'wall' | 'rock' | 'dust' | 'nebula' | 'fixed';
interface Sel {
  type: ElType;
  index: number;
}

const DRAFT_KEY = 'lensing:v1:draft';

const TOOL_ICONS: Record<string, string> = {
  select: '<path d="M5 3l14 8-6 2-2 6Z"/>',
  source: '<circle cx="9" cy="12" r="4"/><path d="M14 12h7M13 8l5-3M13 16l5 3"/>',
  receiver:
    '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>',
  wall: '<path d="M4 20 20 4"/><path d="M7 21 21 7M3 17 17 3" opacity=".5"/>',
  rock: '<path d="M5 15c-1-4 2-8 6-9 4-1 8 2 8 6s-3 7-7 7-6-1-7-4Z"/>',
  dust: '<circle cx="8" cy="9" r="1"/><circle cx="14" cy="7" r="1"/><circle cx="16" cy="13" r="1"/><circle cx="10" cy="15" r="1"/><circle cx="6" cy="14" r="1"/><circle cx="12" cy="11" r="1"/><circle cx="18" cy="9" r="1"/>',
  nebula:
    '<path d="M4 14c2-6 8-8 12-5s5 8 0 9-8-2-12-4Z"/><path d="M8 13c2-2 5-2 7 0" opacity=".6"/>',
  erase: ICONS.trash,
};

function loadDraft(): Level | null {
  try {
    const code = window.localStorage.getItem(DRAFT_KEY);
    if (!code) return null;
    const r = decodeLevel(code);
    return r.ok ? r.level : null;
  } catch {
    return null;
  }
}

function saveDraft(level: Level) {
  try {
    window.localStorage.setItem(DRAFT_KEY, encodeLevel(level));
  } catch {
    // Storage unavailable: the draft lives in memory and in the share code.
  }
}

function starter(): Level {
  return {
    ...emptyLevel(),
    name: 'My first plate',
    sources: [{ x: 140, y: 420, angle: 0, color: 7 }],
    receivers: [{ x: 1040, y: 300, color: 7 }],
    inventory: { moon: 1, star: 1 },
  };
}

export function editorScreen(code: string | null): Screen {
  let level: Level = starter();
  let loadError = '';
  if (code) {
    const r = decodeLevel(code);
    if (r.ok) level = { ...r.level, id: 'custom' };
    else loadError = r.error;
  } else {
    level = loadDraft() ?? starter();
  }
  let tool: Tool = 'select';
  let sel: Sel | null = null;
  let showSolution = false;
  let child: Screen | null = null;
  let drag: {
    sel: Sel;
    part: 'body' | 'a' | 'b' | 'angle';
    dx: number;
    dy: number;
    id: number;
    moved: boolean;
  } | null = null;
  let container: HTMLElement;
  let resizeObs: ResizeObserver | null = null;

  const root = h('section', { class: 'editor', 'aria-label': 'Level editor' });
  const slot = h('div', { class: 'board-slot', 'aria-hidden': 'true' });
  const catcher = h('div', {
    class: 'board-catch',
    style: { pointerEvents: 'auto', touchAction: 'none' },
  });
  const tools = h('aside', { class: 'tools', 'aria-label': 'Editor tools' });
  const nameTitle = h('h2', { class: 'lvl-name' });
  const status = h('p', { class: 'label', role: 'status', style: { margin: '0' } });

  // ------------------------------------------------------------------ model helpers
  const q = (v: number) => Math.round(v);
  const clampX = (v: number) => Math.max(0, Math.min(WORLD_W, q(v)));
  const clampY = (v: number) => Math.max(0, Math.min(WORLD_H, q(v)));

  function commit(next: Level, keepSolution = false) {
    // Any change to the plate invalidates the stored reference solution unless it still wins.
    let lvl = next;
    if (!keepSolution && lvl.solution.length > 0 && !wins(lvl, lvl.solution)) {
      lvl = { ...lvl, solution: [], par: 0 };
    }
    level = lvl;
    saveDraft(level);
    app.renderer.setLevel(level);
    renderPanel();
    app.requestFrame();
  }

  function elementLabel(s: Sel): string {
    const n = s.index + 1;
    switch (s.type) {
      case 'source': {
        const e = level.sources[s.index]!;
        const beams = e.beams ?? 1;
        return `Beacon ${n} · ${colorName(e.color)} · ${Math.round(e.angle)}°${beams > 1 ? ` · fan of ${beams}` : ''}`;
      }
      case 'receiver': {
        const e = level.receivers[s.index]!;
        return `Telescope ${n} · ${colorName(e.color)} · ${e.count ?? 1} beam${(e.count ?? 1) > 1 ? 's' : ''}`;
      }
      case 'wall':
        return `Wall ${n}`;
      case 'rock':
        return `Rock ${n} · r ${level.rocks[s.index]!.r}`;
      case 'dust':
        return `Dust ${n} · r ${level.dust[s.index]!.r}`;
      case 'nebula':
        return `Nebula ${n} · ${colorName(level.nebulae[s.index]!.color)}`;
      case 'fixed':
        return `Fixed ${MASS_SPECS[level.fixed[s.index]!.kind].name.toLowerCase()} ${n}`;
    }
  }

  function allSelections(): Sel[] {
    const out: Sel[] = [];
    level.sources.forEach((_, i) => out.push({ type: 'source', index: i }));
    level.receivers.forEach((_, i) => out.push({ type: 'receiver', index: i }));
    level.walls.forEach((_, i) => out.push({ type: 'wall', index: i }));
    level.rocks.forEach((_, i) => out.push({ type: 'rock', index: i }));
    level.dust.forEach((_, i) => out.push({ type: 'dust', index: i }));
    level.nebulae.forEach((_, i) => out.push({ type: 'nebula', index: i }));
    level.fixed.forEach((_, i) => out.push({ type: 'fixed', index: i }));
    return out;
  }

  function position(s: Sel): [number, number] {
    switch (s.type) {
      case 'source':
        return [level.sources[s.index]!.x, level.sources[s.index]!.y];
      case 'receiver':
        return [level.receivers[s.index]!.x, level.receivers[s.index]!.y];
      case 'wall': {
        const w = level.walls[s.index]!;
        return [(w.x1 + w.x2) / 2, (w.y1 + w.y2) / 2];
      }
      case 'rock':
        return [level.rocks[s.index]!.x, level.rocks[s.index]!.y];
      case 'dust':
        return [level.dust[s.index]!.x, level.dust[s.index]!.y];
      case 'nebula':
        return [level.nebulae[s.index]!.x, level.nebulae[s.index]!.y];
      case 'fixed':
        return [level.fixed[s.index]!.x, level.fixed[s.index]!.y];
    }
  }

  function moveTo(s: Sel, x: number, y: number): Level {
    const L = structuredClone(level);
    switch (s.type) {
      case 'source':
        Object.assign(L.sources[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
      case 'receiver':
        Object.assign(L.receivers[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
      case 'wall': {
        const w = L.walls[s.index]!;
        const [cx, cy] = [(w.x1 + w.x2) / 2, (w.y1 + w.y2) / 2];
        const dx = x - cx;
        const dy = y - cy;
        L.walls[s.index] = {
          x1: clampX(w.x1 + dx),
          y1: clampY(w.y1 + dy),
          x2: clampX(w.x2 + dx),
          y2: clampY(w.y2 + dy),
        };
        break;
      }
      case 'rock':
        Object.assign(L.rocks[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
      case 'dust':
        Object.assign(L.dust[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
      case 'nebula':
        Object.assign(L.nebulae[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
      case 'fixed':
        Object.assign(L.fixed[s.index]!, { x: clampX(x), y: clampY(y) });
        break;
    }
    return L;
  }

  function removeSel(s: Sel) {
    const L = structuredClone(level);
    const key = (
      {
        source: 'sources',
        receiver: 'receivers',
        wall: 'walls',
        rock: 'rocks',
        dust: 'dust',
        nebula: 'nebulae',
        fixed: 'fixed',
      } as const
    )[s.type];
    (L[key] as unknown[]).splice(s.index, 1);
    sel = null;
    commit(L);
    announce(`${elementLabel({ ...s })} removed.`);
  }

  function addAt(t: Tool, x: number, y: number) {
    const L = structuredClone(level);
    let added: Sel | null = null;
    const X = clampX(x);
    const Y = clampY(y);
    if (t === 'source') {
      if (L.sources.length >= 8) return say('A plate can have at most 8 beacons.');
      L.sources.push({ x: X, y: Y, angle: X > WORLD_W / 2 ? 180 : 0, color: 7 });
      added = { type: 'source', index: L.sources.length - 1 };
    } else if (t === 'receiver') {
      if (L.receivers.length >= 8) return say('A plate can have at most 8 telescopes.');
      L.receivers.push({ x: X, y: Y, color: 7 });
      added = { type: 'receiver', index: L.receivers.length - 1 };
    } else if (t === 'wall') {
      L.walls.push({ x1: clampX(X), y1: clampY(Y - 90), x2: clampX(X), y2: clampY(Y + 90) });
      added = { type: 'wall', index: L.walls.length - 1 };
    } else if (t === 'rock') {
      L.rocks.push({ x: X, y: Y, r: 36 });
      added = { type: 'rock', index: L.rocks.length - 1 };
    } else if (t === 'dust') {
      L.dust.push({ x: X, y: Y, r: 70 });
      added = { type: 'dust', index: L.dust.length - 1 };
    } else if (t === 'nebula') {
      L.nebulae.push({ x: X, y: Y, r: 90, color: 1 });
      added = { type: 'nebula', index: L.nebulae.length - 1 };
    } else if (MASS_KINDS.includes(t as MassKind)) {
      if (L.fixed.length >= 8) return say('A plate can have at most 8 fixed masses.');
      L.fixed.push({ kind: t as MassKind, x: X, y: Y });
      added = { type: 'fixed', index: L.fixed.length - 1 };
    }
    if (L.walls.length + L.rocks.length + L.dust.length + L.nebulae.length > 40)
      return say('That is the most scenery a plate can hold.');
    sel = added;
    commit(L);
    audio.tick('drop');
    if (added)
      announce(
        `${elementLabel(added)} added at column ${Math.round(X / 50)}, row ${Math.round(Y / 50)}.`,
      );
  }

  function say(text: string) {
    status.textContent = text;
    announce(text);
  }

  // ------------------------------------------------------------------ hit testing
  function hit(wx: number, wy: number): { sel: Sel; part: 'body' | 'a' | 'b' | 'angle' } | null {
    const tol = 18 / app.renderer.view.scale;
    // Beacon direction handles first.
    for (let i = level.sources.length - 1; i >= 0; i--) {
      const s = level.sources[i]!;
      const a = (s.angle * Math.PI) / 180;
      const hx = s.x + Math.cos(a) * 46;
      const hy = s.y + Math.sin(a) * 46;
      if (sel?.type === 'source' && sel.index === i && Math.hypot(wx - hx, wy - hy) < tol)
        return { sel: { type: 'source', index: i }, part: 'angle' };
    }
    for (let i = level.walls.length - 1; i >= 0; i--) {
      const w = level.walls[i]!;
      if (Math.hypot(wx - w.x1, wy - w.y1) < tol)
        return { sel: { type: 'wall', index: i }, part: 'a' };
      if (Math.hypot(wx - w.x2, wy - w.y2) < tol)
        return { sel: { type: 'wall', index: i }, part: 'b' };
    }
    const near = (x: number, y: number, r: number) => Math.hypot(wx - x, wy - y) < r + tol * 0.6;
    for (let i = level.sources.length - 1; i >= 0; i--)
      if (near(level.sources[i]!.x, level.sources[i]!.y, SOURCE_RADIUS))
        return { sel: { type: 'source', index: i }, part: 'body' };
    for (let i = level.receivers.length - 1; i >= 0; i--)
      if (near(level.receivers[i]!.x, level.receivers[i]!.y, RECEIVER_RADIUS))
        return { sel: { type: 'receiver', index: i }, part: 'body' };
    for (let i = level.fixed.length - 1; i >= 0; i--)
      if (near(level.fixed[i]!.x, level.fixed[i]!.y, MASS_SPECS[level.fixed[i]!.kind].radius))
        return { sel: { type: 'fixed', index: i }, part: 'body' };
    for (let i = level.walls.length - 1; i >= 0; i--) {
      const w = level.walls[i]!;
      const ex = w.x2 - w.x1;
      const ey = w.y2 - w.y1;
      const l2 = ex * ex + ey * ey || 1;
      const t = Math.max(0, Math.min(1, ((wx - w.x1) * ex + (wy - w.y1) * ey) / l2));
      if (Math.hypot(wx - (w.x1 + t * ex), wy - (w.y1 + t * ey)) < WALL_HALF + tol * 0.6)
        return { sel: { type: 'wall', index: i }, part: 'body' };
    }
    for (let i = level.rocks.length - 1; i >= 0; i--)
      if (near(level.rocks[i]!.x, level.rocks[i]!.y, level.rocks[i]!.r))
        return { sel: { type: 'rock', index: i }, part: 'body' };
    for (let i = level.nebulae.length - 1; i >= 0; i--)
      if (near(level.nebulae[i]!.x, level.nebulae[i]!.y, level.nebulae[i]!.r))
        return { sel: { type: 'nebula', index: i }, part: 'body' };
    for (let i = level.dust.length - 1; i >= 0; i--)
      if (near(level.dust[i]!.x, level.dust[i]!.y, level.dust[i]!.r))
        return { sel: { type: 'dust', index: i }, part: 'body' };
    return null;
  }

  // ------------------------------------------------------------------ pointer
  function onDown(e: PointerEvent) {
    if (!e.isPrimary || e.button > 0) return;
    const [wx, wy] = app.renderer.view.toWorld(e.clientX, e.clientY);
    const p = app.renderer.view.plate;
    if (e.clientX < p.x || e.clientX > p.x + p.w || e.clientY < p.y || e.clientY > p.y + p.h)
      return;
    e.preventDefault();
    const found = hit(wx, wy);
    if (tool === 'erase') {
      if (found) removeSel(found.sel);
      return;
    }
    if (tool !== 'select' && !found) {
      addAt(tool, wx, wy);
      return;
    }
    if (found) {
      sel = found.sel;
      const [px, py] = position(found.sel);
      drag = {
        sel: found.sel,
        part: found.part,
        dx: px - wx,
        dy: py - wy,
        id: e.pointerId,
        moved: false,
      };
      try {
        catcher.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      renderPanel();
      app.requestFrame();
    } else {
      sel = null;
      renderPanel();
      app.requestFrame();
    }
  }

  function onMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.id) return;
    const [wx, wy] = app.renderer.view.toWorld(e.clientX, e.clientY);
    drag.moved = true;
    const s = drag.sel;
    let L: Level;
    if (drag.part === 'angle' && s.type === 'source') {
      L = structuredClone(level);
      const src = L.sources[s.index]!;
      let deg = (Math.atan2(wy - src.y, wx - src.x) * 180) / Math.PI;
      deg = Math.round((((deg % 360) + 360) % 360) * 2) / 2;
      src.angle = deg;
    } else if ((drag.part === 'a' || drag.part === 'b') && s.type === 'wall') {
      L = structuredClone(level);
      const w = L.walls[s.index]!;
      if (drag.part === 'a') Object.assign(w, { x1: clampX(wx), y1: clampY(wy) });
      else Object.assign(w, { x2: clampX(wx), y2: clampY(wy) });
    } else {
      L = moveTo(s, wx + drag.dx, wy + drag.dy);
    }
    level = L;
    app.renderer.setLevel(level);
    app.requestFrame();
  }

  function onUp(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.id) return;
    const moved = drag.moved;
    drag = null;
    if (moved) commit(level);
    else renderPanel();
  }

  // ------------------------------------------------------------------ keyboard on the element list
  function onListKey(e: KeyboardEvent, s: Sel) {
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const d = arrows[e.key];
    if (d) {
      e.preventDefault();
      const step = e.shiftKey ? 25 : e.altKey ? 1 : 5;
      const [dx, dy] = app.renderer.view.screenStepToWorld(d[0] * step, d[1] * step);
      const [x, y] = position(s);
      sel = s;
      commit(moveTo(s, x + dx, y + dy));
      const btn = tools.querySelector<HTMLButtonElement>(
        `.elements-list button[data-key="${s.type}-${s.index}"]`,
      );
      btn?.focus();
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      removeSel(s);
    }
  }

  // ------------------------------------------------------------------ panel
  function stepper(
    label: string,
    value: number,
    min: number,
    max: number,
    onChange: (v: number) => void,
  ): HTMLElement {
    const out = h('output', { text: String(value) });
    const dec = h('button', { type: 'button', 'aria-label': `Fewer ${label}` }, icon(ICONS.minus));
    const inc = h('button', { type: 'button', 'aria-label': `More ${label}` }, icon(ICONS.plus));
    dec.addEventListener('click', () => value > min && onChange(value - 1));
    inc.addEventListener('click', () => value < max && onChange(value + 1));
    return h('span', { class: 'stepper', role: 'group', 'aria-label': label }, dec, out, inc);
  }

  function swatches(value: number, onPick: (c: number) => void): HTMLElement {
    const wrap = h('div', { class: 'swatches', role: 'radiogroup', 'aria-label': 'Colour' });
    for (const c of [7, 1, 2, 4, 3, 5, 6]) {
      const b = h('button', {
        class: 'swatch',
        type: 'button',
        role: 'radio',
        'aria-checked': String(c === value),
        'aria-label': colorName(c),
        title: colorName(c),
        style: { background: beamColor(c, store.settings.contrast) },
      });
      b.addEventListener('click', () => onPick(c));
      wrap.append(b);
    }
    return wrap;
  }

  function range(
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onInput: (v: number) => void,
  ): HTMLElement {
    const input = h('input', {
      type: 'range',
      min: String(min),
      max: String(max),
      step: String(step),
      value: String(value),
      'aria-label': label,
    }) as HTMLInputElement;
    const out = h('output', { text: String(value) });
    input.addEventListener('input', () => {
      out.textContent = input.value;
      onInput(Number(input.value));
    });
    input.addEventListener('change', () => commit(level));
    return h(
      'span',
      { style: { display: 'inline-flex', alignItems: 'center', gap: '8px' } },
      input,
      out,
    );
  }

  function liveEdit(fn: (L: Level) => void) {
    const L = structuredClone(level);
    fn(L);
    level = L;
    app.renderer.setLevel(level);
    app.requestFrame();
  }

  function propsFor(s: Sel): HTMLElement {
    const box = h(
      'div',
      { class: 'props' },
      h('p', { class: 'label', style: { margin: '6px 0 0' }, text: elementLabel(s) }),
    );
    const row = (label: string, ...kids: HTMLElement[]) =>
      h('div', { class: 'props-row' }, h('span', { class: 'label', text: label }), ...kids);
    if (s.type === 'source') {
      const e = level.sources[s.index]!;
      box.append(
        row(
          'Colour',
          swatches(e.color, (c) =>
            commit({
              ...level,
              sources: level.sources.map((x, i) => (i === s.index ? { ...x, color: c } : x)),
            }),
          ),
        ),
        row(
          'Angle',
          range('Beacon angle in degrees', e.angle, 0, 359.5, 0.5, (v) =>
            liveEdit((L) => (L.sources[s.index]!.angle = v)),
          ),
        ),
        row(
          'Beams',
          stepper('beams', e.beams ?? 1, 1, 3, (v) =>
            commit({
              ...level,
              sources: level.sources.map((x, i) =>
                i === s.index
                  ? v > 1
                    ? { ...x, beams: v, spread: x.spread ?? 10 }
                    : { x: x.x, y: x.y, angle: x.angle, color: x.color }
                  : x,
              ),
            }),
          ),
        ),
      );
      if ((e.beams ?? 1) > 1) {
        box.append(
          row(
            'Spread',
            range('Fan spread in degrees', e.spread ?? 10, 2, 40, 0.5, (v) =>
              liveEdit((L) => (L.sources[s.index]!.spread = v)),
            ),
          ),
        );
      }
    } else if (s.type === 'receiver') {
      const e = level.receivers[s.index]!;
      box.append(
        row(
          'Needs',
          swatches(e.color, (c) =>
            commit({
              ...level,
              receivers: level.receivers.map((x, i) => (i === s.index ? { ...x, color: c } : x)),
            }),
          ),
        ),
        row(
          'Beams',
          stepper('beams needed', e.count ?? 1, 1, 3, (v) =>
            commit({
              ...level,
              receivers: level.receivers.map((x, i) =>
                i === s.index
                  ? v > 1
                    ? { ...x, count: v }
                    : { x: x.x, y: x.y, color: x.color }
                  : x,
              ),
            }),
          ),
        ),
      );
    } else if (s.type === 'wall') {
      const rot = (deg: number) => {
        const L = structuredClone(level);
        const w = L.walls[s.index]!;
        const cx = (w.x1 + w.x2) / 2;
        const cy = (w.y1 + w.y2) / 2;
        const a = (deg * Math.PI) / 180;
        const r = (x: number, y: number): [number, number] => [
          cx + (x - cx) * Math.cos(a) - (y - cy) * Math.sin(a),
          cy + (x - cx) * Math.sin(a) + (y - cy) * Math.cos(a),
        ];
        const [x1, y1] = r(w.x1, w.y1);
        const [x2, y2] = r(w.x2, w.y2);
        L.walls[s.index] = { x1: clampX(x1), y1: clampY(y1), x2: clampX(x2), y2: clampY(y2) };
        commit(L);
      };
      const len = (f: number) => {
        const L = structuredClone(level);
        const w = L.walls[s.index]!;
        const cx = (w.x1 + w.x2) / 2;
        const cy = (w.y1 + w.y2) / 2;
        L.walls[s.index] = {
          x1: clampX(cx + (w.x1 - cx) * f),
          y1: clampY(cy + (w.y1 - cy) * f),
          x2: clampX(cx + (w.x2 - cx) * f),
          y2: clampY(cy + (w.y2 - cy) * f),
        };
        commit(L);
      };
      box.append(
        row(
          'Turn',
          h('button', { class: 'btn', type: 'button', onclick: () => rot(-15), text: '−15°' }),
          h('button', { class: 'btn', type: 'button', onclick: () => rot(15), text: '+15°' }),
        ),
        row(
          'Length',
          h('button', { class: 'btn', type: 'button', onclick: () => len(0.8), text: 'Shorter' }),
          h('button', { class: 'btn', type: 'button', onclick: () => len(1.25), text: 'Longer' }),
        ),
        h('p', {
          class: 'label',
          style: { margin: '2px 0' },
          text: 'Drag the ends on the plate to reshape.',
        }),
      );
    } else if (s.type === 'rock' || s.type === 'dust' || s.type === 'nebula') {
      const list = s.type === 'rock' ? level.rocks : s.type === 'dust' ? level.dust : level.nebulae;
      const e = list[s.index]!;
      const [min, max] = s.type === 'rock' ? [10, 110] : s.type === 'dust' ? [20, 190] : [30, 240];
      box.append(
        row(
          'Size',
          range('Radius', e.r, min, max, 1, (v) =>
            liveEdit((L) => {
              const target = s.type === 'rock' ? L.rocks : s.type === 'dust' ? L.dust : L.nebulae;
              target[s.index]!.r = v;
            }),
          ),
        ),
      );
      if (s.type === 'nebula') {
        const n = level.nebulae[s.index]!;
        box.append(
          row(
            'Passes',
            swatches(n.color, (c) =>
              commit({
                ...level,
                nebulae: level.nebulae.map((x, i) => (i === s.index ? { ...x, color: c } : x)),
              }),
            ),
          ),
        );
      }
    } else if (s.type === 'fixed') {
      const e = level.fixed[s.index]!;
      const kinds = h('div', { class: 'seg', role: 'radiogroup', 'aria-label': 'Kind of mass' });
      for (const k of MASS_KINDS) {
        const b = h('button', {
          type: 'button',
          role: 'radio',
          'aria-checked': String(k === e.kind),
          text: MASS_SPECS[k].name,
        });
        b.addEventListener('click', () =>
          commit({
            ...level,
            fixed: level.fixed.map((x, i) => (i === s.index ? { ...x, kind: k } : x)),
          }),
        );
        kinds.append(b);
      }
      box.append(row('Kind', kinds));
    }
    box.append(
      h(
        'div',
        { class: 'props-row' },
        h(
          'button',
          { class: 'btn danger', type: 'button', onclick: () => removeSel(s) },
          icon(ICONS.trash),
          'Remove',
        ),
      ),
    );
    return box;
  }

  function renderPanel() {
    nameTitle.replaceChildren(h('span', { class: 'lvl-num', text: 'Editor' }), level.name);
    const toolbar = h('div', { class: 'toolbar', role: 'toolbar', 'aria-label': 'Tools' });
    const toolDefs: [Tool, string][] = [
      ['select', 'Select'],
      ['source', 'Beacon'],
      ['receiver', 'Telescope'],
      ['wall', 'Wall'],
      ['rock', 'Rock'],
      ['dust', 'Dust'],
      ['nebula', 'Nebula'],
      ['moon', 'Moon'],
      ['star', 'Star'],
      ['giant', 'Giant'],
      ['hole', 'Black hole'],
      ['void', 'Void'],
      ['erase', 'Erase'],
    ];
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    for (const [t, label] of toolDefs) {
      const b = h('button', {
        class: 'tool',
        type: 'button',
        'aria-pressed': String(tool === t),
        'data-tool': t,
        title: label,
      });
      if (TOOL_ICONS[t]) b.append(icon(TOOL_ICONS[t]!));
      else b.append(trayIcon(t as MassKind, 24, dpr));
      b.append(h('span', { text: label }));
      b.addEventListener('click', () => {
        tool = t;
        renderPanel();
        say(
          t === 'select'
            ? 'Select: drag to move, drag ends and handles to shape.'
            : t === 'erase'
              ? 'Erase: click an element to remove it.'
              : `Click the plate to add: ${label.toLowerCase()}.`,
        );
      });
      toolbar.append(b);
    }

    const list = h('ul', { class: 'elements-list', 'aria-label': 'Elements on the plate' });
    for (const s of allSelections()) {
      const cur = sel?.type === s.type && sel.index === s.index;
      const b = h('button', {
        type: 'button',
        'data-key': `${s.type}-${s.index}`,
        'aria-current': String(cur),
        text: elementLabel(s),
        title: 'Arrow keys move, Delete removes',
      });
      b.addEventListener('click', () => {
        sel = s;
        renderPanel();
        app.requestFrame();
        tools
          .querySelector<HTMLButtonElement>(
            `.elements-list button[data-key="${s.type}-${s.index}"]`,
          )
          ?.focus();
      });
      b.addEventListener('keydown', (e) => onListKey(e, s));
      list.append(h('li', {}, b));
    }
    if (list.children.length === 0)
      list.append(
        h('li', { class: 'label', text: 'Empty plate: pick a tool and click the plate.' }),
      );

    const inv = h('div', { class: 'inv-grid' });
    for (const k of MASS_KINDS) {
      inv.append(
        h(
          'span',
          { class: 'inv-item' },
          trayIcon(k, 28, dpr),
          stepper(
            `${MASS_SPECS[k].plural.toLowerCase()} in the tray`,
            level.inventory[k] ?? 0,
            0,
            5,
            (v) => {
              const invNext = { ...level.inventory, [k]: v };
              if (v === 0) delete invNext[k];
              commit({ ...level, inventory: invNext });
            },
          ),
        ),
      );
    }

    const name = h('input', {
      type: 'text',
      value: level.name,
      maxlength: '40',
      'aria-label': 'Plate name',
    }) as HTMLInputElement;
    name.addEventListener('change', () =>
      commit({ ...level, name: name.value.trim() || 'Untitled plate' }, true),
    );

    const t = traceLevel(level, []);
    const v = validateLevel(level);
    const problems: string[] = [];
    if (level.sources.length === 0) problems.push('Add a beacon.');
    if (level.receivers.length === 0) problems.push('Add a telescope.');
    if (!v.ok && problems.length === 0) problems.push(v.errors[0]!);
    if (problems.length === 0 && t.won)
      problems.push(
        'The empty plate already lights every telescope: move something so players have work to do.',
      );
    if (problems.length === 0 && Object.values(level.inventory).every((n) => !n))
      problems.push('Put at least one mass in the tray.');
    const solved = level.solution.length > 0;

    const solToggle = h('button', {
      class: 'switch',
      type: 'button',
      role: 'switch',
      'aria-checked': String(showSolution && solved),
      'aria-label': 'Show reference solution',
      disabled: !solved,
    });
    solToggle.addEventListener('click', () => {
      showSolution = !showSolution;
      renderPanel();
      app.requestFrame();
    });

    tools.replaceChildren(
      toolbar,
      sel
        ? propsFor(sel)
        : h('p', {
            class: 'label',
            style: { margin: '2px 0' },
            text: tool === 'select' ? 'Select an element to edit it.' : '',
          }),
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'label', text: 'Tray (masses the player gets)' }),
        inv,
      ),
      h('div', { class: 'field' }, h('span', { class: 'label', text: 'Name' }), name),
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'label', text: 'Elements (arrow keys move the focused one)' }),
        list,
      ),
      h(
        'div',
        { class: 'props-row' },
        h('span', { class: 'label', text: 'Reference' }),
        h('span', {
          text: solved
            ? `Saved: weight ${weightOf(level.solution)} = par`
            : 'None yet: win a test play to save one.',
        }),
        solToggle,
      ),
      problems.length
        ? h('p', {
            class: 'label',
            style: { color: 'var(--danger)', margin: '4px 0' },
            role: 'status',
            text: problems[0]!,
          })
        : status,
      h(
        'div',
        { class: 'props-row' },
        h(
          'button',
          {
            class: 'btn primary',
            type: 'button',
            disabled: problems.length > 0,
            onclick: startTest,
          },
          icon(ICONS.play),
          'Test play',
        ),
        h(
          'button',
          { class: 'btn', type: 'button', onclick: openShare },
          icon(ICONS.share),
          'Share / import',
        ),
        h('button', { class: 'btn ghost', type: 'button', onclick: newBlank }, 'New blank plate'),
      ),
    );
  }

  function newBlank() {
    sel = null;
    tool = 'source';
    commit({ ...emptyLevel(), name: 'Untitled plate', inventory: { moon: 1 } });
    say('Blank plate. Click to place a beacon.');
  }

  // ------------------------------------------------------------------ share
  function openShare() {
    const q0 = quantizeLevel(level);
    const code = encodeLevel(q0);
    const base = `${location.origin}${location.pathname}`;
    const playUrl = `${base}#/custom/${code}`;
    const editUrl = `${base}#/editor/${code}`;
    const field = (label: string, value: string, id: string) => {
      const ta = h('textarea', {
        id,
        readonly: true,
        rows: '3',
        'aria-label': label,
      }) as HTMLTextAreaElement;
      ta.value = value;
      const copy = h('button', { class: 'btn', type: 'button' }, icon(ICONS.copy), 'Copy');
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(value);
          copy.lastChild!.textContent = 'Copied';
        } catch {
          ta.select();
          copy.lastChild!.textContent = 'Selected: press Ctrl+C';
        }
      });
      return h(
        'div',
        { class: 'field' },
        h('label', { class: 'label', for: id, text: label }),
        ta,
        h('div', {}, copy),
      );
    };
    const importBox = h('textarea', {
      id: 'import-code',
      rows: '3',
      placeholder: 'Paste a share code or link',
      'aria-label': 'Share code or link to import',
    }) as HTMLTextAreaElement;
    const importMsg = h('p', { class: 'label', role: 'status' });
    const importBtn = h('button', {
      class: 'btn primary',
      type: 'button',
      text: 'Load into editor',
    });
    let closeFn = () => {};
    importBtn.addEventListener('click', () => {
      const raw = importBox.value.trim();
      const m = raw.match(/#\/(?:custom|editor)\/([A-Za-z0-9_-]+)/);
      const r = decodeLevel(m ? m[1]! : raw);
      if (!r.ok) {
        importMsg.textContent = `That code does not work: ${r.error}.`;
        importMsg.style.color = 'var(--danger)';
        return;
      }
      closeFn();
      sel = null;
      commit({ ...r.level, id: 'custom' }, true);
      history.replaceState(null, '', `#/editor/${encodeLevel(r.level)}`);
      say(`Loaded "${r.level.name}".`);
    });
    const body = h(
      'div',
      { class: 'dlg-body' },
      h('p', {
        text: 'Anyone with the link can play this plate. The code holds the whole level, including the reference solution and par if you saved one.',
      }),
      field('Play link', playUrl, 'share-play'),
      field('Edit link', editUrl, 'share-edit'),
      field('Share code', code, 'share-code'),
      h('h3', { text: 'Import' }),
      h('div', { class: 'field' }, importBox, h('div', {}, importBtn), importMsg),
    );
    closeFn = openDialog('Share this plate', body);
  }

  // ------------------------------------------------------------------ test play
  function startTest() {
    const testLevel = { ...quantizeLevel(level), id: 'custom' };
    container.replaceChildren();
    child = gameScreen({
      mode: 'test',
      level: testLevel,
      backHref: '#/editor',
      onTestDone: (solution) => {
        child?.unmount();
        child = null;
        if (solution && wins(level, solution)) {
          level = { ...level, solution, par: weightOf(solution) };
          saveDraft(level);
          announce(`Reference solution saved. Par is ${weightOf(solution)}.`);
        }
        container.replaceChildren(root);
        app.renderer.setLevel(level);
        renderPanel();
        layoutSelf();
        app.requestFrame();
      },
    });
    child.mount(container);
    requestAnimationFrame(() => {
      child?.layout?.();
      app.requestFrame();
    });
  }

  function layoutSelf() {
    const r = slot.getBoundingClientRect();
    app.renderer.setSlot({ x: r.left, y: r.top, w: r.width, h: r.height }, true);
    // The catcher covers the plate only.
    const p = app.renderer.view.plate;
    // Reset the stylesheet's `inset: 0` first, then place the catcher exactly over the plate.
    catcher.style.inset = 'auto';
    Object.assign(catcher.style, {
      position: 'absolute',
      left: `${p.x}px`,
      top: `${p.y}px`,
      width: `${p.w}px`,
      height: `${p.h}px`,
    });
  }

  return {
    title: 'Editor',
    mount(c) {
      container = c;
      const hud = h(
        'header',
        { class: 'hud' },
        h(
          'a',
          { class: 'icon-btn', href: '#/', 'aria-label': 'Title', title: 'Title' },
          icon(ICONS.back),
        ),
        h(
          'div',
          { class: 'titleblock' },
          h('h1', { class: 'wordmark', text: 'Lensing' }),
          nameTitle,
        ),
        h(
          'div',
          { class: 'actions' },
          h(
            'button',
            {
              class: 'icon-btn',
              type: 'button',
              'aria-label': 'How to play',
              onclick: () => openHelp('play'),
            },
            icon(ICONS.help),
          ),
        ),
      );
      root.append(hud, slot, tools, catcher);
      catcher.addEventListener('pointerdown', onDown);
      catcher.addEventListener('pointermove', onMove);
      catcher.addEventListener('pointerup', onUp);
      catcher.addEventListener('pointercancel', onUp);
      c.append(root);
      // The tool panel can change height (it sits under the plate on phones): refit the plate.
      resizeObs = new ResizeObserver(() => {
        if (child) return;
        layoutSelf();
        app.requestFrame();
      });
      resizeObs.observe(slot);
      app.renderer.setLevel(level);
      renderPanel();
      if (loadError)
        say(`That share code did not work (${loadError}). Starting from a fresh plate instead.`);
      markReady();
    },
    unmount() {
      resizeObs?.disconnect();
      child?.unmount();
      app.renderer.setLevel(null);
    },
    layout() {
      if (child) {
        child.layout?.();
        return;
      }
      layoutSelf();
    },
    frame(time) {
      if (child) return child.frame(time);
      const placed: Mass[] = showSolution ? level.solution : [];
      const trace = traceLevel(level, placed);
      app.renderer.render(
        {
          level,
          placed,
          trace,
          drag: null,
          focus: null,
          hints: [],
          litAt: level.receivers.map(() => -1),
          selection: sel,
        },
        app.renderOptions(time),
        true,
      );
      drawSelection();
      return !store.reducedMotion;
    },
  };

  /** Selection outline and handles, drawn over the board. */
  function drawSelection() {
    if (!sel) return;
    const r = app.renderer;
    const ctx = r.context;
    const v = r.view;
    ctx.save();
    ctx.setTransform(r.pixelRatio, 0, 0, r.pixelRatio, 0, 0);
    ctx.strokeStyle = '#e8c36a';
    ctx.fillStyle = '#e8c36a';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    const ring = (x: number, y: number, rad: number) => {
      const [sx, sy] = v.toScreen(x, y);
      ctx.beginPath();
      ctx.arc(sx, sy, rad * v.scale + 6, 0, Math.PI * 2);
      ctx.stroke();
    };
    const handle = (x: number, y: number) => {
      const [sx, sy] = v.toScreen(x, y);
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(sx, sy, 6, 0, Math.PI * 2);
      ctx.fill();
    };
    const s = sel;
    if (s.type === 'source') {
      const e = level.sources[s.index];
      if (e) {
        ring(e.x, e.y, SOURCE_RADIUS);
        const a = (e.angle * Math.PI) / 180;
        const [ax, ay] = v.toScreen(e.x, e.y);
        const [bx, by] = v.toScreen(e.x + Math.cos(a) * 46, e.y + Math.sin(a) * 46);
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
        handle(e.x + Math.cos(a) * 46, e.y + Math.sin(a) * 46);
      }
    } else if (s.type === 'receiver') {
      const e = level.receivers[s.index];
      if (e) ring(e.x, e.y, RECEIVER_RADIUS);
    } else if (s.type === 'wall') {
      const w = level.walls[s.index];
      if (w) {
        handle(w.x1, w.y1);
        handle(w.x2, w.y2);
      }
    } else if (s.type === 'fixed') {
      const e = level.fixed[s.index];
      if (e) ring(e.x, e.y, MASS_SPECS[e.kind].radius);
    } else {
      const list = s.type === 'rock' ? level.rocks : s.type === 'dust' ? level.dust : level.nebulae;
      const e = list[s.index];
      if (e) ring(e.x, e.y, e.r);
    }
    ctx.restore();
  }
}
